<script setup lang="ts">
import { computed } from "vue";
import PublicHeader from '@/components/navigation/PublicHeader.vue';
import Footer from '@/components/layout/Footer.vue';
import ConnectionBanner from '@/components/layout/ConnectionBanner.vue';
import ErrorBoundary from "@/components/common/ErrorBoundary.vue";
import { useRoute } from "vue-router";
import routeManager from "@/router/route-manager";

const route = useRoute();
const showHeaderFooter = computed<boolean>( () => !( routeManager.isSignInPath( route.path ) ) );
const isHomePage = computed<boolean>( () => routeManager.isHomePath( route.path ) );
</script>

<template>
  <!--
    index.html carries viewport-fit=cover, so these insets are live. The header handles its own
    top inset (it is sticky, so the padding has to live on the header itself); the shell covers
    the sides in landscape and the home indicator at the bottom.
  -->
  <div class="public-shell flex min-h-dvh flex-col">
    <!-- Keyboard users had no way past the header nav on every route. -->
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:rounded focus:bg-base-100 focus:px-4 focus:py-2 focus:no-underline focus:shadow-lg"
    >Skip to main content</a>

    <PublicHeader v-if="showHeaderFooter" class="print:hidden" />

    <!-- UX roadmap #58: offline, or content that keeps failing to sync. -->
    <ConnectionBanner />

    <!--
      The min-height keeps the footer below the fold while a page is still loading. Without it the
      footer filled the viewport under a loading page and was then shoved off-screen when the
      content arrived: a layout shift of 0.54 on the home page and 0.56 on an article.
    -->
    <main
      id="main-content"
      tabindex="-1"
      :class="[ 'flex-1', isHomePage ? '' : 'm-2 sm:m-4 gap-4 print:m-0', showHeaderFooter ? 'min-h-[calc(100dvh-var(--public-header-h))] print:min-h-0' : 'public-shell-end' ]"
    >
      <!-- UX roadmap #17: a page that fails to render says so and offers a retry; the next route, query included,
           clears it. A page that did not fail is never remounted by it. -->
      <ErrorBoundary name="page" :reset-key="route.fullPath" title="This page couldn't load." testid="page">
        <slot></slot>
      </ErrorBoundary>
    </main>

    <Footer v-if="showHeaderFooter" class="public-shell-end print:hidden" />
  </div>
</template>

<style scoped>
.public-shell {
  /* PublicHeader: h-16, its 1px bottom border and the top safe-area inset it pads itself with. */
  --public-header-h: calc(4rem + 1px + env(safe-area-inset-top, 0px));

  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
}

/*
  UX roadmap #27, as in Private.vue: the support launcher (fixed, bottom-right) covered the end of the page, the
  footer's last line or, where there is no footer (sign-in), the form's last control. The last block leaves its corner
  free (zendesk-widget.ts sets the property once the launcher loads; unset, nothing changes). Padding inside the footer,
  so its own background runs under the launcher. At every width: it was below lg only, but from lg the launcher is the
  wider "Help" pill, which covered the end of the page as well (2026-10-19).
*/
.public-shell-end {
  padding-bottom: var(--support-launcher-inset, 0px);
}
</style>
